/**
 * Mock data for offer features (materials availability & gardener skills).
 * Frontend-only; replace with real API/inventory when backend is ready.
 */

// --- Material availability (mock) ---

export type MaterialAvailabilityStatus = 'in_stock' | 'short' | 'out_of_stock';

export interface MockMaterialAvailability {
	status: MaterialAvailabilityStatus;
	/** When short: how much is missing (e.g. { quantity: 3, unit: 'm²' }) */
	shortBy?: { quantity: number; unit: string };
	/** Human-readable lead time to source (e.g. '5–7 days') */
	leadTime?: string;
}

/**
 * Returns mock availability for a material. Uses name/quantity/unit to vary demo data.
 * In production this would call an inventory API.
 */
export function getMockMaterialAvailability(
	name: string,
	quantity: number,
	unit: string
): MockMaterialAvailability {
	const key = (name || '').toLowerCase().trim();
	// Vary by material type for demo: some in stock, some short, some with lead time
	if (key.includes('gravel') || key.includes('sand') || key.includes('mulch')) {
		return { status: 'in_stock', leadTime: '1–2 days' };
	}
	if (key.includes('paving') || key.includes('slab') || key.includes('stone')) {
		return { status: 'short', shortBy: { quantity: 3, unit: unit || 'm²' }, leadTime: '5–7 days' };
	}
	if (key.includes('plant') || key.includes('shrub') || key.includes('tree')) {
		return { status: 'in_stock', leadTime: '3–5 days' };
	}
	if (key.includes('soil') || key.includes('compost')) {
		return { status: 'in_stock' };
	}
	if (key.includes('fence') || key.includes('timber') || key.includes('wood')) {
		return { status: 'out_of_stock', leadTime: '7–10 days' };
	}
	// Default: alternate by quantity for variety
	const idx = Math.abs((name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % 3;
	if (idx === 0) return { status: 'in_stock', leadTime: '2–4 days' };
	if (idx === 1) return { status: 'short', shortBy: { quantity: 2, unit: unit || 'pcs' }, leadTime: '5–7 days' };
	return { status: 'out_of_stock', leadTime: '7–14 days' };
}

// --- Gardener skills ---

/** Fixed list of skills that a project might need (for demo). In production derive from plan phases/tasks. */
export const MOCK_SKILLS_NEEDED_FOR_PROJECT: string[] = [
	'hardscaping',
	'planting',
	'irrigation',
	'pruning',
	'lawn_care'
];

/** Single source of truth for skills: key, display label, and description for Settings. */
export const SKILLS_WITH_DESCRIPTIONS: Array<{ key: string; label: string; description: string }> = [
	{
		key: 'hardscaping',
		label: 'Hardscaping',
		description: 'Ground construction: excavation, sub-base, concrete, paving, porcelain/stone paths, patios, steps and retaining walls.'
	},
	{
		key: 'planting',
		label: 'Planting',
		description: 'Bed prep and planting of trees, shrubs, perennials, groundcovers, seasonal colour and mixed borders.'
	},
	{
		key: 'irrigation',
		label: 'Irrigation',
		description: 'Design and installation of drip lines, sprinklers, soaker hoses and irrigation controllers.'
	},
	{
		key: 'pruning',
		label: 'Pruning',
		description: 'Structural and seasonal pruning of trees, shrubs and hedges for health, safety and shape.'
	},
	{
		key: 'lawn_care',
		label: 'Lawn care',
		description: 'Turf laying, repair and renovation, seeding, mowing, scarifying and lawn feeding.'
	},
	{
		key: 'design',
		label: 'Design',
		description: 'Garden and landscape design: layout, planting plans, material choices and overall look & feel.'
	},
	{
		key: 'maintenance',
		label: 'Maintenance',
		description: 'Regular garden care including weeding, mulching, deadheading, tidying and general upkeep.'
	},
	{
		key: 'project_management',
		label: 'Project management',
		description: 'Planning and coordinating projects, timelines, trades, suppliers and client communication.'
	},
	{
		key: 'sustainable_gardening',
		label: 'Sustainable gardening',
		description: 'Eco-friendly practices: composting, soil health, water-wise design and use of native / climate-suitable plants.'
	},
	{
		key: 'fencing_structures',
		label: 'Fencing & structures',
		description: 'Building and installing fences, screens, pergolas, decks, benches, raised beds, outdoor kitchens and other timber or metal garden structures.'
	},
	{
		key: 'water_features',
		label: 'Water features',
		description: 'Design and installation of ornamental ponds, fountains, cascades and other decorative water elements.'
	},
	{
		key: 'lighting',
		label: 'Lighting',
		description: 'Low-voltage outdoor lighting: path lights, spotlights, deck and feature lighting, including basic cabling and transformers.'
	},
	{
		key: 'drainage',
		label: 'Drainage',
		description: 'Managing surface and ground water: grading, soakaways, French drains and drainage around hard surfaces and structures.'
	}
];

/** All selectable skill keys for offer wizard etc. Must match backend AI allowed list. */
export const SKILLS_OPTIONS: string[] = SKILLS_WITH_DESCRIPTIONS.map((s) => s.key);

/**
 * @deprecated Use real skills from gardener.skills. Fallback for backward compatibility.
 */
export function getMockGardenerSkills(gardenerId: number): string[] {
	return ['planting'];
}

/**
 * Skills covered by the selected team (union of all selected gardeners' skills).
 * Uses gardenerSkillsMap: id -> skills[] (from API or offer-level overrides).
 */
export function getSkillsCovered(
	selectedGardenerIds: number[],
	gardenerSkillsMap: (id: number) => string[]
): string[] {
	const set = new Set<string>();
	for (const id of selectedGardenerIds) {
		for (const skill of gardenerSkillsMap(id)) {
			set.add(skill);
		}
	}
	return Array.from(set);
}

/**
 * Skills needed for the project but not covered by the selected gardeners.
 */
export function getMissingSkills(
	selectedGardenerIds: number[],
	gardenerSkillsMap: (id: number) => string[],
	skillsNeeded: string[] = MOCK_SKILLS_NEEDED_FOR_PROJECT
): string[] {
	const covered = new Set(getSkillsCovered(selectedGardenerIds, gardenerSkillsMap));
	return skillsNeeded.filter(s => !covered.has(s));
}

/** Legacy: same as getSkillsCovered but uses mock data (for backward compat). */
export function getMockSkillsCovered(selectedGardenerIds: number[]): string[] {
	return getSkillsCovered(selectedGardenerIds, getMockGardenerSkills);
}

/** Legacy: same as getMissingSkills but uses mock data (for backward compat). */
export function getMockMissingSkills(
	selectedGardenerIds: number[],
	skillsNeeded: string[] = MOCK_SKILLS_NEEDED_FOR_PROJECT
): string[] {
	return getMissingSkills(selectedGardenerIds, getMockGardenerSkills, skillsNeeded);
}
